// The two bits of upkeep that keep transient state honest: what a tool going
// down does to a half-drawn run, and what a ply does to the selection.
//
// Upkeep only — nothing here is called by a click. The Session calls it after
// every store change and whenever `core.building` flips.

import type { GameState } from "src/engine/rules/commands";
import {
  beltDirAt,
  depositAt,
  hasBlueprint,
  hasStructure,
  heapAt,
  NO_BELT
} from "src/engine/rules/industry/state";
import { moveFrom, moveTo } from "src/engine/rules/move";
import { pieceAt } from "src/engine/rules/position";

import type { SessionCore } from "./core";

/**
 * Is there ground here to keep looking at? `groundModel`'s four kinds, asked
 * of the state directly — there is no side to ask it for, and the owner is
 * not what decides whether a square is still there.
 */
const groundLeft = (state: GameState, sq: number) =>
  beltDirAt(state.industry, sq) !== NO_BELT ||
  hasBlueprint(state.industry, sq) ||
  heapAt(state.industry, sq) > 0 ||
  depositAt(state.industry, sq) > 0;

/**
 * Putting the belt tool down abandons a half-drawn run.
 *
 * An anchor lights the same highlight a selection does, so one left behind
 * is a lit square with nothing that can act on it — and the click that would
 * have finished the run now means something else entirely. Both halves of a
 * two-click gesture belong to the tool that started it.
 *
 * Idempotent, so running it on every store change is the same as running it
 * on the flip: neither an anchor nor a `lay` is ever armed without the tool.
 */
function dropRun(core: SessionCore): boolean {
  if (core.building) return false;
  let moved = false;
  if (core.lineAnchor !== undefined) {
    core.lineAnchor = undefined;
    moved = true;
  }
  if (core.pending?.kind === "lay") {
    core.pending = undefined;
    moved = true;
  }
  return moved;
}

/**
 * A selection follows its piece across a ply instead of being dropped.
 *
 * Selecting is looking, and what you are looking at is a *piece* — but the
 * selection is stored as a **square**, so a ply left it pointing at whatever
 * happened to be standing there afterwards, which is usually nothing. You
 * lost the piece you were reading every time anybody moved.
 *
 * A move is in `history`, and that is what is followed (belts carry no pieces,
 * §31 R4).
 *
 * The selection is cleared, not carried, when the piece stops existing:
 * taken where it stood, or gone for a reason nothing reported — an en
 * passant victim is not on the square the capture names, and a surrendered
 * army leaves no move records at all. So the square is checked at the end
 * rather than trusted.
 */
function followPiece(core: SessionCore): boolean {
  const state = core.state;
  const moves = state.history;
  const from = core.seenMoves;
  core.seenMoves = moves.length;
  // Undo is truncate-and-replay, so the log shrinks. There is nothing to
  // follow backwards; the square is left alone and the check below decides.
  //
  // **The check runs whether or not anything moved.** A heap mined dry, a
  // lane crushed and an undo all leave the selection on a square that has
  // nothing left to look at, and none of them is a move of the selection's.
  const fresh = moves.length > from ? moves.slice(from) : [];

  const square = core.selected;
  if (square === undefined) return false;
  let at: number | undefined = square;
  for (const move of fresh) {
    if (moveFrom(move) === at) at = moveTo(move);
    // Taken where it stood. The square still has a piece on it — the one
    // that did the taking — so leaving the anchor alone would silently
    // hand the selection to the enemy.
    else if (moveTo(move) === at) {
      at = undefined;
      break;
    }
  }
  if (at !== undefined) {
    const stillThere =
      pieceAt(state.position, at) !== 0 ||
      hasStructure(state.industry, at) ||
      groundLeft(state, at);
    if (!stillThere) at = undefined;
  }
  if (at === square) return false;
  core.selected = at;
  return true;
}

/** Both effects, once; `changed()` only if something moved. */
export function followSelection(core: SessionCore): void {
  const dropped = dropRun(core);
  const followed = followPiece(core);
  if (dropped || followed) core.changed();
}
